import { LocalDate } from 'js-joda';
import _ from 'lodash';

import {
  DetailsPageMode,
  isReservationVariety,
  offeringConsumerDetailPageRelativeLink,
} from '@tock/shared/utils/offerings';
import { core } from '@tock/coretypes';
import { getConsumerURL, getJceBaseURL } from 'utils/urlUtils';
import AppSettings from 'settings/AppSettings';

export interface GetDirectLinkOptions {
  preview?: boolean;
  detailsPageMode?: DetailsPageMode;
}

export function getDirectLink(
  ticket: core.TicketType,
  businessDomain: string,
  options: GetDirectLinkOptions = {}
) {
  const baseUrl = getConsumerURL(AppSettings.API_BASE, businessDomain);
  const previewQueryString = '?previewMode=true';
  return `${baseUrl}${offeringConsumerDetailPageRelativeLink(
    ticket,
    undefined,
    options.detailsPageMode
  )}${options.preview ? previewQueryString : ''}`;
}

export function getJCEPrivateEventURL(ticket: core.TicketType, business: core.Business) {
  const baseUrl = getJceBaseURL(AppSettings.API_BASE);
  const location = `${business.city}-${business.state}`.toLowerCase();
  return `${baseUrl}/cities/${location}/venues/${business.domainName}/event/${ticket.id}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function experienceToShortCode(name: any) {
  const splitName = _.split(name, /[ |-]/);
  let shortCode = _.reduce(
    splitName,
    (acc, word) => {
      const alphaNumericWord = _.replace(word, /[^0-9a-zA-Z]*/, '');
      if (_.isEmpty(alphaNumericWord)) {
        return acc;
      } else {
        return acc + _.toUpper(alphaNumericWord[0]);
      }
    },
    ''
  );
  if (shortCode.length < 2) {
    shortCode = _.padEnd(shortCode, 2, '-');
  }
  return _.truncate(shortCode, { length: 5, omission: '' });
}

export function sortEvents(events: core.TicketType[]) {
  if (_.size(events) === 0) {
    return undefined;
  }
  const eventsBeforeToday = _.filter(events, (event) => {
    if (!_.isEmpty(event.onlyBusinessDay)) {
      const eventDate = LocalDate.parse(event.onlyBusinessDay);
      const today = LocalDate.now();
      return eventDate.isBefore(today) || eventDate.equals(today);
    }
    return true;
  });
  const eventsAfterToday = _.filter(events, (event) => {
    if (!_.isEmpty(event.onlyBusinessDay)) {
      return LocalDate.parse(event.onlyBusinessDay).isAfter(LocalDate.now());
    }
    return false;
  });

  const sortedEventsBeforeToday = _.orderBy(eventsBeforeToday, 'onlyBusinessDay', 'desc');
  const sortedEventsAfterToday = _.orderBy(eventsAfterToday, 'onlyBusinessDay');

  return _.concat(sortedEventsAfterToday, sortedEventsBeforeToday);
}

export function getSelectedExperience(tickets: core.TicketType[], experienceId: string | number) {
  const selectedExperienceId = _.toNumber(experienceId);

  return _.find(tickets, (ticket) => {
    return selectedExperienceId === ticket.id;
  });
}

/**
 * Determines whether the given experience has a large party deposit and if so, return it (or false otherwise).
 *
 * Logic was extracted from `<OfferingPricingForm />`.
 * */
export function getLargePartyDeposit(
  experience: core.ITicketType,
  isFlexBusiness: boolean
): number | false {
  if (!isFlexBusiness || !isReservationVariety(experience.variety)) {
    return false;
  }

  return experience.largePartyPriceCents || false;
}

export function getDefaultPrice(ticketType: core.ITicketType) {
  if ((ticketType.ticketTypePriceConfig?.length || 0) > 0) {
    return (
      _.sortBy(ticketType.ticketTypePriceConfig, (config) => config.minPartySizeInclusive ?? 1)?.[0]
        .priceCents || 0
    );
  }

  return ticketType.defaultPrice || 0;
}
